import React, { useEffect, useState, FC } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import axios, { type AxiosResponse } from 'axios';

import { Breadcrumb } from '@shared/components/bootstrap';

import { PageHeader } from '@admin/components/helpers/page_header';
import { Spinner } from '@admin/components/spinner';
import { useAxiosRequest } from '@admin/hooks/use_axios_request';
import { useNavigate } from '@admin/hooks/use_navigate';
import { useTitle } from '@admin/hooks/use_title';

import { LocationForm, type LocationFormData } from './form';

type Location = {
  id: number;
  name?: string;
  value: string;
  located?: {
    object?: {
      id: number;
      name: string;
    };
  };
} & LocationFormData;

const useLocationRequest = (locationID: number | string) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<Location>> | undefined>();
  useEffect(() => {
    setRequest(axios.get<Location>(`/locations/${locationID}.json`));
  }, [locationID]);
  return useAxiosRequest(request);
};

const useLocationCreateRequest = (locationID: number | string) => {
  const [request, setRequest] = useState<Promise<AxiosResponse<LocationFormData>> | undefined>();

  const save = (data: LocationFormData) => {
    const request = axios.patch<Location>(`/locations/${locationID}.json`, data);
    setRequest(request);
    return request;
  };

  return [save, useAxiosRequest(request)] as const;
};

export const LocationEdit: FC = () => {
  useTitle('Location');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | undefined>();
  const { response } = useLocationRequest(id);
  const [save, { loading }] = useLocationCreateRequest(id);
  const location = response?.data;
  const warehouse = location?.located?.object;

  const onSave = async (data: LocationFormData) => {
    if (loading) return;
    try {
      setError(undefined);
      const response = await save(data);
      navigate(`/locations/${response.data.id}`);
    } catch (error) {
      if (axios.isAxiosError(error)) setError(error.response?.data?.message);
      else setError('An unknown error occurred');
    }
  };

  if (!location) return <Spinner />;

  return (
    <>
      <PageHeader>
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/warehouses">Warehouses</NavLink>
          </Breadcrumb.Item>
          {warehouse && (
            <Breadcrumb.Item>
              <NavLink to={`/warehouses/${warehouse.id}`}>{warehouse.name}</NavLink>
            </Breadcrumb.Item>
          )}
          {warehouse && (
            <Breadcrumb.Item>
              <NavLink to={`/warehouses/${warehouse.id}/locations`}>Locations</NavLink>
            </Breadcrumb.Item>
          )}
          <Breadcrumb.Item>
            <NavLink to={`/locations/${id}`}>{location.value ?? '-'}</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Edit</Breadcrumb.Item>
        </Breadcrumb>
      </PageHeader>
      <LocationForm data={location} error={error} loading={loading} onSave={onSave} />
    </>
  );
};
